import { ScatterplotLayer, PolygonLayer, GeoJsonLayer } from "@deck.gl/layers";
import { RasterLayer, ParticleLayer, ImageType } from "weatherlayers-gl";

import type { DomainsGeoJSON, GridstatsResponse, LayerType } from "../../../types";
import { toTextureData, toUVTextureData } from "../../../api/hindcasts";
import { createCirclePoints } from "../../../utils/circlePoints";

export function makeLocationLayer(lon: number, lat: number) {
  return new ScatterplotLayer({
    id: "location",
    data: [{ position: [lon, lat] }],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getPosition: (d: any) => d.position,
    getRadius: 4000,
    getFillColor: [255, 255, 255, 255],
    getLineColor: [0, 0, 0],
    filled: true,
    stroked: true,
    radiusMinPixels: 5,
    radiusMaxPixels: 20,
    lineWidthMinPixels: 2,
    pickable: false,
  });
}

export function makeRadiusLayer(lon: number, lat: number, radiusKm: number) {
  const contour = createCirclePoints(lon, lat, radiusKm);
  return new PolygonLayer({
    id: "radius",
    data: [{ contour }],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getPolygon: (d: any) => d.contour,
    stroked: true,
    filled: true,
    extruded: false,
    getLineColor: [80, 80, 80, 200],
    getFillColor: [100, 100, 100, 40],
    lineWidthMinPixels: 1,
    pickable: false,
  });
}

/** Transparent stroke-only layer for picking domain edges — filled interior is not pickable. */
export function makeDomainsPickLayer(domains: DomainsGeoJSON) {
  return new GeoJsonLayer({
    id: "domains-pick",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: domains as any,
    filled: false,
    stroked: true,
    getLineColor: [0, 0, 0, 0],
    lineWidthMinPixels: 10,
    pickable: true,
  });
}

export function makeGridstatsLayers(
  gridstats: GridstatsResponse,
  layerType: LayerType,
  showParticles = true
) {
  const { bounds, meta } = gridstats;
  const [minLon, minLat, maxLon, maxLat] = bounds;

  // weatherlayers-gl RasterLayer/ParticleLayer don't support wrapLongitude.
  // For global datasets, tile manually at -360, 0, +360 degree offsets so
  // the layer renders continuously when panning past the antimeridian.
  const offsets = maxLon - minLon > 355 ? [-360, 0, 360] : [0];

  if (layerType === "wave") {
    const hsImage = toTextureData(gridstats.hs);
    const uvWaveImage = toUVTextureData(gridstats.uv_wave);

    // weatherlayers-gl ParticleLayer creates internal WebGL framebuffers that
    // break when multiple instances coexist — keep exactly one particle layer.
    // RasterLayer tiles fine with multiple copies; particles stay at offset 0.
    return [
      ...offsets.map((off) =>
        new RasterLayer({
          id: `wave-hs-${off}`,
          image: hsImage,
          imageType: ImageType.SCALAR,
          bounds: [minLon + off, minLat, maxLon + off, maxLat],
          palette: meta.wave_palette,
          opacity: 0.65,
          pickable: false,
        })
      ),
      ...(showParticles ? [new ParticleLayer({
        id: "wave-particles",
        image: uvWaveImage,
        imageType: ImageType.VECTOR,
        bounds: [minLon, minLat, maxLon, maxLat],
        numParticles: 7000,
        maxAge: 30,
        speedFactor: 15,
        color: [255, 255, 255, 120],
        width: 7,
        animate: true,
        pickable: false,
      })] : []),
    ];
  }

  // wind layer — only rendered when wind data is available
  if (layerType === "wind" && gridstats.wspd && gridstats.uv_wind) {
    const wspdImage = toTextureData(gridstats.wspd);
    const uvWindImage = toUVTextureData(gridstats.uv_wind);

    return [
      ...offsets.map((off) =>
        new RasterLayer({
          id: `wind-wspd-${off}`,
          image: wspdImage,
          imageType: ImageType.SCALAR,
          bounds: [minLon + off, minLat, maxLon + off, maxLat],
          palette: meta.wind_palette,
          opacity: 0.65,
          pickable: false,
        })
      ),
      ...(showParticles ? [new ParticleLayer({
        id: "wind-particles",
        image: uvWindImage,
        imageType: ImageType.VECTOR,
        bounds: [minLon, minLat, maxLon, maxLat],
        numParticles: 8000,
        maxAge: 30,
        speedFactor: 10,
        color: [255, 255, 255, 200],
        width: 1,
        animate: true,
        pickable: false,
      })] : []),
    ];
  }

  return [];
}
